import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Share2, Loader2, Link2, UserPlus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, shareLink, copyText } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function Referrals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.myReferrals().then(setData).catch((e) => setErr(e.message)); }, []);

  const copy = async () => { toast((await copyText(data.link)) ? "Referral link copied" : data.link); };
  const share = async () => { const r = await shareLink(data.link, "Join me on AuraMax"); if (r === "copied") toast("Referral link copied"); else if (r === "failed") toast(data.link); };

  if (err) return <div className="py-24 text-center text-sm text-am-text2">{err}</div>;
  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>;
  const inTop = data.leaderboard.some((r) => r.user.user_id === user?.user_id);

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:px-6" data-testid="referrals-page">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-am-text2 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="font-display text-2xl font-extrabold text-white">Invite friends</h1>
      <p className="mt-1 text-sm text-am-text2">Your referral code is your username. Share your link and bring your people to AuraMax.</p>

      <div className="mt-5 rounded-3xl border border-am-border bg-am-surface p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-am-text3">Your link</div>
        <div className="mt-2 flex items-center gap-2 rounded-2xl border border-am-border bg-am-surface2 px-3 py-2.5">
          <Link2 className="h-4 w-4 shrink-0 text-am-text3" />
          <span className="min-w-0 flex-1 truncate text-sm text-white" data-testid="referral-link">{data.link}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={copy} data-testid="referral-copy" className="flex items-center justify-center gap-2 rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white hover:bg-white/5"><Copy className="h-4 w-4" /> Copy</button>
          <button onClick={share} data-testid="referral-share" className="flex items-center justify-center gap-2 rounded-full aura-gradient py-2.5 text-sm font-bold text-black"><Share2 className="h-4 w-4" /> Share</button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {[["Signups", data.stats.total, "referral-stat-total"], ["Qualified", data.stats.qualified, "referral-stat-qualified"], ["Pending", data.stats.pending, "referral-stat-pending"]].map(([l, v, t]) => (
          <div key={l} data-testid={t} className="rounded-2xl border border-am-border bg-am-surface2 p-3 text-center">
            <div className="font-display text-2xl font-extrabold text-white">{v}</div>
            <div className="text-xs text-am-text2">{l}</div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-bold text-white md:text-lg">Top referrers</h2>
          {data.my_rank && <span className="text-xs text-am-text2" data-testid="referral-my-rank">Your rank: <span className="font-bold text-white">#{data.my_rank}</span></span>}
        </div>
        <div className="overflow-hidden rounded-2xl border border-am-border bg-am-surface/60">
          {data.leaderboard.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-am-text3" data-testid="referral-board-empty">No qualified referrals yet. Be the first on the board.</p>
          ) : data.leaderboard.map((r, i) => <Row key={r.user.user_id} r={r} me={r.user.user_id === user?.user_id} border={i > 0} navigate={navigate} />)}
          {!inTop && data.my_rank && (
            <div className="border-t border-am-border/60 px-4 py-1 text-center text-[10px] uppercase tracking-wider text-am-text3">· · ·</div>
          )}
          {!inTop && data.my_rank && user && (
            <Row r={{ rank: data.my_rank, user: { ...user, picture: user.picture }, qualified: data.stats.qualified }} me border navigate={navigate} />
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-am-border bg-am-surface2 p-4">
        <h3 className="text-sm font-bold text-white">How it works</h3>
        <ol className="mt-2 space-y-2 text-sm text-am-text2">
          <li className="flex gap-2"><UserPlus className="mt-0.5 h-4 w-4 shrink-0 aura-text" /> A friend joins through your link and automatically follows you.</li>
          <li className="flex gap-2"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 aura-text" /> Their signup counts as <b className="text-white">qualified</b> once they verify their email, add a profile photo and make their first post.</li>
          <li className="flex gap-2"><Share2 className="mt-0.5 h-4 w-4 shrink-0 aura-text" /> Qualified referrals rank you on the leaderboard here and on Market.</li>
        </ol>
      </div>
    </div>
  );
}

function Row({ r, me, border, navigate }) {
  return (
    <div data-testid={me ? "referral-row-me" : `referral-row-${r.rank}`} className={cn("flex items-center gap-3 px-4 py-2.5", border && "border-t border-am-border", me && "bg-white/[0.04]")}>
      <span className={cn("w-7 text-center text-sm font-bold", r.rank <= 3 ? "aura-text" : "text-am-text2")}>#{r.rank}</span>
      <button onClick={() => navigate(`/@${r.user.username}`)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
        <AuraRing src={mediaUrl(r.user.picture) || undefined} size={34} ring={1.5} fallback={r.user.name} />
        <div className="min-w-0"><div className="flex items-center gap-1 truncate text-sm font-semibold text-white">{r.user.name}{r.user.is_verified && <VerifiedBadge size={12} />}{me && <span className="ml-1 rounded-full bg-white/10 px-1.5 text-[10px] text-am-text2">you</span>}</div><div className="truncate text-xs text-am-text2">@{r.user.username}</div></div>
      </button>
      <span className="text-sm font-bold text-white">{r.qualified}</span>
    </div>
  );
}
